"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdminContext = void 0;
exports.AdminContextProvider = AdminContextProvider;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const cancelable_promise_1 = require("cancelable-promise");
const SessionContext_1 = require("./SessionContext");
const UserContext_1 = require("./UserContext");
const MyProfile_1 = __importDefault(require("../api/MyProfile"));
const react_native_webview_1 = __importDefault(require("react-native-webview"));
exports.AdminContext = react_1.default.createContext(null);
const MenuWrapper = react_1.default.forwardRef((props, ref) => {
    const [menu, setMenu] = react_1.default.useState(null);
    react_1.default.useImperativeHandle(ref, () => ({ setMenu }));
    return menu;
});
function AdminContextProvider(props) {
    const sessionContext = react_1.default.useContext(SessionContext_1.SessionContext);
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const portalEnabled = userContext?.adminExperience == 'both' ||
        userContext?.adminExperience == 'elevate-edge';
    const navigation = react_1.default.useRef(null);
    const sessionTimestamp = react_1.default.useRef(0);
    const navigateTimestamp = react_1.default.useRef(0);
    const pendoTimestamp = react_1.default.useRef(0);
    const frame = react_1.default.useRef(null);
    const menu = react_1.default.useRef(null);
    const resizer = react_1.default.useRef(null);
    const loadPromise = react_1.default.useRef(null);
    const [data, setData] = react_1.default.useState(null);
    const [status, setStatus] = react_1.default.useState('none');
    const startTimer = react_1.default.useRef(null);
    const heartbeatTimer = react_1.default.useRef(null);
    const prismUrl = react_1.default.useRef(null);
    const prismBaseUrl = react_1.default.useRef(null);
    const [webViewUrl, setWebViewUrl] = react_1.default.useState('');
    const [floatMenu, setFloatMenu] = react_1.default.useState(false);
    const [employeeMenu, setEmployeeMenu] = react_1.default.useState(null);
    const navigate = (url) => {
        if (!url?.startsWith('http')) {
            url = (react_native_1.Platform.OS == 'web' ? 'about:blank' : '');
        }
        else {
            let d = '&';
            if (!url.includes('?'))
                d = '?';
            url += d + 'hideNavHeader=true';
        }
        navigateTimestamp.current = (new Date()).getTime();
        return url;
    };
    const reset = () => {
        setStatus('loading');
        loadPromise.current?.cancel();
        clearTimeout(startTimer.current);
        clearInterval(heartbeatTimer.current);
        setData(null);
        navigate(null);
    };
    const startSession = () => {
        reset();
        loadPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(t => MyProfile_1.default.getAdminDetails(t))).then(response => {
            setData(response);
            if (response?.prismUserType != 'M') {
                setStatus('disabled');
                return;
            }
            setStatus('starting');
            prismBaseUrl.current = response.prismUrl.split('/#')?.[0];
            if (react_native_1.Platform.OS == 'web') {
                frame.current.contentWindow.location.replace(response.prismUrl);
            }
            else {
                setWebViewUrl(response.prismUrl);
            }
            sessionTimestamp.current = (new Date()).getTime();
            navigateTimestamp.current = (new Date()).getTime();
            startTimer.current = setTimeout(() => {
                setStatus('ready');
            }, (10 * 1000));
            heartbeatTimer.current = setInterval(() => {
                let sessionExpire = (28 * 60 * 1000);
                let now = (new Date()).getTime();
                if (now > (navigateTimestamp.current + sessionExpire)) {
                    startSession();
                }
            }, (1 * 60 * 1000));
        }).catch((error) => {
            setStatus('error');
            console.log(error);
        });
    };
    react_1.default.useEffect(() => {
        if (sessionContext.userId &&
            userContext?.employeeDone &&
            userContext?.clientDone &&
            userContext?.isAdmin &&
            portalEnabled) {
            setFloatMenu(react_native_1.Platform.OS != 'web' || react_native_1.Dimensions.get('window').width < 1280);
            startSession();
        }
        return () => reset();
    }, [
        sessionContext.userId,
        userContext?.employeeDone,
        userContext?.clientDone,
        userContext?.adminExperience
    ]);
    react_1.default.useEffect(() => {
        if (react_native_1.Platform.OS == 'web' && portalEnabled) {
            resizer.current = new ResizeObserver(() => {
                setFloatMenu(react_native_1.Dimensions.get('window').width < 1024);
            });
            let messageHandler = (event) => {
                if (event?.origin == prismBaseUrl.current &&
                    event?.data?.includes?.('"type":"load"')) {
                    pendoTimestamp.current = (new Date()).getTime();
                    try {
                        let data = JSON.parse(event?.data);
                        if (data?.url?.includes?.('/#/ess/home')) {
                            setTimeout(() => setStatus('ready'), 1000);
                        }
                    }
                    catch { }
                }
            };
            resizer.current.observe(document.body);
            window.addEventListener('message', messageHandler);
            return () => {
                resizer.current?.unobserve?.(document.body);
                resizer.current?.disconnect?.();
                window.removeEventListener('message', messageHandler);
            };
        }
    }, [userContext?.adminExperience]);
    const signOutHandler = react_1.default.useCallback(() => {
        let clientId = userContext.client?.id;
        let employeeId = userContext.employee?.employeeId;
        async_storage_1.default.removeItem(`${clientId}-${employeeId}-employees-sort`);
        async_storage_1.default.removeItem(`${clientId}-${employeeId}-employees-filters`);
        async_storage_1.default.removeItem(`${clientId}-${employeeId}-employees-search`);
    }, [
        userContext?.employeeDone,
        userContext?.clientDone,
        userContext?.adminExperience
    ]);
    react_1.default.useEffect(() => {
        const handle = react_native_1.DeviceEventEmitter.addListener('event.signOut', signOutHandler);
        return () => handle?.remove();
    }, [signOutHandler]);
    return ((0, jsx_runtime_1.jsxs)(exports.AdminContext.Provider, { value: {
            data,
            status,
            initialized: (data && (status == 'ready')),
            prismBaseUrl: prismBaseUrl.current,
            setNavigation: (n) => navigation.current = n,
            setMenu: (m) => menu.current?.setMenu(m),
            setEmployeeMenu: (m) => setEmployeeMenu(m),
            employeeMenu: employeeMenu,
            floatMenu: floatMenu,
            isPrismManager: () => (data?.prismUserType == 'M'),
            hasPrismHrRole: (r) => (data?.prismHrRoles?.includes?.(r)),
            hasPrismProcess: (p) => (data?.prismProcesses?.includes?.(p)),
            navigate: (url) => {
                if (status != 'ready') {
                    return null;
                }
                else {
                    if (url != null) {
                        url = prismBaseUrl.current + url;
                    }
                    prismUrl.current = url;
                    return navigate(prismUrl.current);
                }
            }
        }, children: [props.children, react_native_1.Platform.OS == 'web'
                &&
                    (0, jsx_runtime_1.jsx)("iframe", { ref: frame, style: {
                            position: 'absolute',
                            border: 'none',
                            visibility: 'hidden',
                            opacity: 0
                        } })
                ||
                    (0, jsx_runtime_1.jsx)(react_native_1.View, { ref: frame, style: {
                            position: 'absolute',
                            top: -128, left: -128,
                            width: 64, height: 64
                        }, children: (0, jsx_runtime_1.jsx)(react_native_webview_1.default, { source: { uri: webViewUrl }, startInLoadingState: true, allowFileAccess: true, geolocationEnabled: false, hideKeyboardAccessoryView: true, setSupportMultipleWindows: false, overScrollMode: "never", onMessage: () => null, onNavigationStateChange: (event) => {
                                if (event?.url?.includes?.('/#/ess/home')) {
                                    setStatus('ready');
                                }
                                else if (event?.url?.includes?.('/#/ess/manager/')) {
                                    navigateTimestamp.current = (new Date()).getTime();
                                }
                                else if (event?.url?.includes?.('/#/ess/login')) {
                                    startSession();
                                }
                            } }) }), (0, jsx_runtime_1.jsx)(MenuWrapper, { ref: menu })] }));
}
;
